import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { SyntheticEvent, useMemo, useState } from 'react';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import {
  Comment,
  Heart,
  HeartFill,
  Icon,
  PinFill,
  VerticalEllipsis,
} from 'chatie-design-system/icon';

import ChatEnterPad from '@components/chat/ChatEnterPad';
import ActionSheet, { ActionSheetOption } from '@components/common/ActionSheet';
import ImageCarousel from '@components/common/ImageCarousel';
import SwipeableListContainer from '@components/common/SwipableListContainer';
import { CharacterTag } from '@components/modal/CharacterTagSelectionModal';
import ImageModal from '@components/modal/ImageModal';

import { Platform } from '@constants';
import { media } from '@styles/css';
import { useGlobalContext } from '@contexts/global';
import { useMe } from '@utils/hook';
import { useWebView } from '@utils/webview';

import {
  ICharacterPost,
  IChatCharacter,
  IChatProfile,
  IChatTag,
  IComment,
  IEFinderObject,
} from '../../model';
import { getPostImages } from '../../utils/community';
import { formatDate } from '../../utils/date';
import { trimStringByLine } from '../../utils/misc';
import Avatar, { AvatarSize } from '../common/Avatar';
import Nickname from '../user/Nickname';
import MentionCards from './MentionCards';
import MentionText from './MentionText';
import PollDisplay from './PollDisplay';

const postCSS = css`
  position: relative;
`;

const postFixedCSS = (theme: Theme) => css`
  ${Typography.body12};
  color: ${theme.var.gray600};
  margin-left: ${Spacing.xhsmall}px;
`;

const postUserCSS = (theme: Theme) =>
  css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${Spacing.medium}px;

    .profile {
      display: flex;
      align-items: center;
    }

    .maker-badge {
      padding: 0 4px;
      display: flex;
      align-items: center;
      height: 18px;
      border: 1px solid ${theme.colors.main500};
      border-radius: ${Radius.xsmall}px;

      ${Typography.body11};
      font-weight: 600;
      line-height: 14.85px;
      color: ${theme.colors.main500};
    }

    .nickname {
      margin-left: ${Spacing.xsmall}px;

      .fixedContainer {
        display: flex;
        align-items: center;
        gap: ${Spacing.xxsmall}px;
      }

      small {
        display: block;
        margin-top: ${Spacing.xxsmall}px;
        color: ${theme.var.gray500};
        ${Typography.body12};
      }
    }
  `;

const postContentCSS = css`
  ${Typography.body14}
`;

const postContentMoreBtnCSS = (theme: Theme) => css`
  color: ${theme.var.gray500};

  &:before {
    content: '\\00a0';
  }
`;

const postPictureCSS =
  (isClip: boolean, imageHeight: number) => (theme: Theme) =>
    css`
      margin-top: ${Spacing.small}px;
      position: relative;
      ${imageHeight ? `height: ${imageHeight}px` : ''};
      background-color: ${theme.var.gray400};
      border-radius: 9px;

      ${!isClip
        ? css`
            & > span {
              position: unset !important;
              & > img {
                position: unset !important;
                height: auto !important;
                object-fit: contain !important;
              }
            }
          `
        : css`
            padding-bottom: 60%;
          `}

      ${media.tablet} {
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
        border-radius: 12px;
        overflow: hidden;
      }
      ${media.desktop} {
        max-width: 480px;
        border-radius: 16px;
      }
    `;

const postButtonCSS = (theme: Theme) => css`
  margin-top: 8px;
  color: ${theme.var.gray500};

  button {
    ${Typography.body12};
  }

  button + button {
    margin-left: ${Spacing.xsmall}px;
  }
`;

const tagCSS = css`
  display: flex;
  align-items: center;
  margin-right: ${Spacing.xsmall}px;
  margin-top: 4px;
`;

const ctaCSS = css`
  border-radius: ${Radius.small}px;
  margin-bottom: ${Spacing.xsmall}px;
  margin-top: 14px;
`;
const commentPreviewCSS = (theme: Theme) => css`
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  gap: ${Spacing.xsmall}px;
  line-height: 100%;
  overflow: hidden;
  white-space: nowrap;

  .user-name {
    ${Typography.h6};
  }

  .content {
    ${Typography.body14};
    color: ${theme.var.gray500};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
export type PostCardAction = 'fix' | 'unfix' | 'modify' | 'delete' | 'report';

export type CharacterPostProps = {
  playerId: number;
  clip?: boolean;
  isMyPage?: boolean;
  onLike?: (post: ICharacterPost) => void;
  handleActionSheet?: (value: PostCardAction, id: number) => void;
  tags?: IChatTag[];
  handleClickTag?: (id: number, name: string) => void;
  playButton?: boolean;
  character?: IChatCharacter;
  handleClickPlay?: (cbaracter: IChatCharacter | IChatProfile) => void;
  handleClickImg?: (img: IEFinderObject<'image'>) => void;
  hasFixedAction?: boolean;
  commentPreview?: IComment[];
  imageHeight?: number;
  showFixedIcon?: boolean;
  onPollVote?: (post: ICharacterPost) => void;
} & ICharacterPost;

const CharacterPost = ({
  playerId,
  clip: isClip = false,
  onLike,
  handleActionSheet,
  handleClickTag,
  playButton,
  tags,
  character,
  handleClickPlay,
  commentPreview,
  handleClickImg,
  hasFixedAction = true,
  imageHeight,
  showFixedIcon = true,
  onPollVote,
  ..._post
}: CharacterPostProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data: me } = useMe();

  const post = _post as ICharacterPost;
  const {
    id,
    user,
    content,
    likeCount,
    commentCount,
    isFixed,
    isLiked,
    isUpdated,
    created,
  } = post;

  const isMaker = playerId === user?.id;
  const isMyCharacter = playerId === me?.id;
  const isMyPost = me?.id === user?.id;
  const tagHeight = 28;
  const { platform } = useGlobalContext();
  const { web } = useWebView();
  const router = useRouter();

  const postImages = useMemo(() => getPostImages(post), [post]);
  const hasImages = postImages.length > 0;

  const [isContentOpen, setContentOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);

  const [content1, content2] = useMemo(
    () => trimStringByLine(content, hasImages ? 2 : 5),
    [content, hasImages]
  );

  const actions = useMemo<ActionSheetOption<PostCardAction>[]>(() => {
    if (!me) {
      return [];
    }

    if (isMyPost) {
      return [
        ...(isMyCharacter && hasFixedAction
          ? ([
              isFixed
                ? { label: t('고정 해제하기'), value: 'unfix' }
                : { label: t('고정하기'), value: 'fix' },
            ] as ActionSheetOption<PostCardAction>[])
          : []),
        { label: t('수정하기'), value: 'modify' },
        { label: t('삭제하기'), value: 'delete', isDanger: true },
      ];
    } else {
      return [
        ...(isMyCharacter && hasFixedAction
          ? ([
              isFixed
                ? { label: t('고정 해제하기'), value: 'unfix' }
                : { label: t('고정하기'), value: 'fix' },
            ] as ActionSheetOption<PostCardAction>[])
          : []),
        { label: t('신고하기'), value: 'report', isDanger: true },
      ];
    }
  }, [isFixed, isMyCharacter, isMyPost, me, t]);

  const handleClickUserProfile = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (platform === Platform.web) {
      router.push(`/users/${user?.id}?tab=play`);
    } else {
      web.openPage(`/users/${user?.id}?tab=play`);
    }
  };

  return (
    <article css={postCSS}>
      <div css={postUserCSS}>
        <div className="profile" onClick={handleClickUserProfile}>
          <Avatar size={AvatarSize.medium} {...user} />
          <div className="nickname">
            <div className="fixedContainer">
              {isMaker && <div className="maker-badge">메이커</div>}
              <h6>
                <Nickname {...user} />
              </h6>
              {isFixed && showFixedIcon && (
                <p css={postFixedCSS} className="icon-text">
                  <Icon
                    icon={PinFill}
                    size={Sizes.small}
                    color={theme.var.gray600}
                  />
                  <span>{t('고정됨')}</span>
                </p>
              )}
            </div>
            <small>
              {created ? formatDate(created) : ''}
              {isUpdated && ' 수정됨'}
            </small>
          </div>
        </div>
        {handleActionSheet && (
          <ActionSheet
            name={`post-card-action-${post.id}`}
            options={actions}
            onSelect={(value) => handleActionSheet(value, id)}
          >
            {(open) => (
              <button className="icon-btn" onClick={open}>
                <Icon
                  icon={VerticalEllipsis}
                  color={theme.var.gray400}
                  size={Sizes.small}
                />
              </button>
            )}
          </ActionSheet>
        )}
      </div>
      {content && (
        <>
          {isClip ? (
            <p css={postContentCSS} className="multiline">
              <MentionText content={content1} />
              {!isContentOpen && content2 && (
                <button
                  css={postContentMoreBtnCSS}
                  className="text-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setContentOpen(true);
                  }}
                >
                  ...{t('더보기')}
                </button>
              )}
              <br />
              {isContentOpen && <MentionText content={content2} />}
              {isContentOpen && (
                <button
                  css={postContentMoreBtnCSS}
                  className="text-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setContentOpen(false);
                  }}
                >
                  {t('접기')}
                </button>
              )}
            </p>
          ) : (
            <p css={postContentCSS} className="multiline">
              <MentionText content={content} />
            </p>
          )}
          <MentionCards content={content} />
        </>
      )}
      {post.poll && (
        <PollDisplay
          poll={post.poll}
          communityId={id}
          onVote={(updatedPoll) => {
            onPollVote?.({ ...post, poll: updatedPoll });
          }}
        />
      )}

      {hasImages && (
        <div css={postPictureCSS(isClip, imageHeight as number)}>
          <ImageCarousel
            images={postImages}
            onClickImage={(index) => {
              if (handleClickImg) {
                handleClickImg(postImages[index]);
              } else {
                setImageModalIndex(index);
                setImageModalOpen(true);
              }
            }}
          />
        </div>
      )}

      <div css={postButtonCSS}>
        <button
          className="text-btn icon-text"
          onClick={(e) => {
            e.stopPropagation();
            onLike?.(post);
          }}
        >
          <Icon
            icon={isLiked ? HeartFill : Heart}
            size={Sizes.small}
            color={isLiked ? theme.colors.red500 : theme.var.gray500}
          />
          <span>{likeCount || t('좋아요')}</span>
        </button>
        <button className="text-btn icon-text">
          <Icon icon={Comment} size={Sizes.small} color={theme.var.gray500} />
          <span>{commentCount || t('댓글달기')}</span>
        </button>
      </div>
      {!!commentPreview?.length && (
        <>
          {/* {commentPreview?.map((comment) => {
            return ( */}
          <div css={commentPreviewCSS}>
            <div className="user-name">{commentPreview[0].user.nickname}</div>
            <div className="content">
              <MentionText content={commentPreview[0].content} />
            </div>
          </div>
          {/* );
          })} */}
        </>
      )}
      {!!tags?.length && (
        <SwipeableListContainer>
          {tags?.map((item, index) => {
            const handleClick = (e: SyntheticEvent) => {
              e.stopPropagation();
              if (handleClickTag) {
                handleClickTag(item.id, item.name);
              }
            };
            return (
              <div css={tagCSS}>
                <CharacterTag
                  name={item.name}
                  key={`tag_${index}`}
                  height={tagHeight}
                  onClick={handleClick}
                />
              </div>
            );
          })}
        </SwipeableListContainer>
      )}
      {playButton && character && (
        <ChatEnterPad
          onlyName={true}
          onClickChatEnter={handleClickPlay}
          enterText={'놀러가기'}
          wrapperCss={ctaCSS}
          playCount={character?.stat?.message_count}
          character={character}
        />
      )}

      {/* 이미지 확대 모달 */}
      {hasImages && !handleClickImg && (
        <ImageModal
          images={postImages}
          initialIndex={imageModalIndex}
          open={isImageModalOpen}
          onClose={() => setImageModalOpen(false)}
        />
      )}
    </article>
  );
};

export default CharacterPost;
